import { useMutation, useQuery } from "@tanstack/react-query"
import { Link, useLocation } from "@tanstack/react-router"
import { type ReactNode, useState } from "react"
import { Logo } from "@/components/logo"
import { ErrorText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardTitle } from "@/components/ui/card"
import {
  type ConsentRequest,
  consentClient,
  consentRequest,
  EXPIRED_TEXT,
  isExpiredLinkError,
  OAUTH_CONSENT_PATH,
  postConsent,
  publicClientQuery,
  rawSearch,
} from "@/lib/oauth"
import { sessionQuery } from "@/lib/queries"

/** The light page around the OAuth steps, as `/pair`: the logo, one card. */
export function OAuthShell({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 px-4 py-10">
      <Link to="/" className="text-ink no-underline hover:text-ink">
        <Logo />
      </Link>
      <Card className="w-full max-w-md">{children}</Card>
    </main>
  )
}

export function OAuthExpired({ detail }: { detail?: string | undefined }) {
  return (
    <>
      <CardTitle>Couldn't connect</CardTitle>
      <p className="mt-2 text-sm leading-relaxed text-ink-2">{detail ?? EXPIRED_TEXT}</p>
    </>
  )
}

/**
 * /oauth/consent?<signed query> (external MCP spec §6.2): may this app (Claude Code, Codex, Claude, ChatGPT, …) use
 * Minga as you? A verified connector is named by its callback ("Claude (claude.ai)"), a CLI client by its registered
 * name "on this computer", any other app by the host of its callback with a warning, its registered name only as "calls
 * itself" (decision 0046 §3, amended 2026-10-12 and 2026-10-21). Allow posts the page's query unchanged as
 * `oauth_query`; the answer is where the browser goes next (the app's callback with a code, or `access_denied` on
 * Cancel). The route guarantees a session.
 */
export function OAuthConsentPage() {
  const searchStr = useLocation({ select: (l) => l.searchStr })
  const query = rawSearch(OAUTH_CONSENT_PATH, searchStr)
  const request = consentRequest(query)
  return (
    <OAuthShell>{request ? <ConsentCard request={request} query={query} /> : <OAuthExpired />}</OAuthShell>
  )
}

function ConsentCard({ request, query }: { request: ConsentRequest; query: string }) {
  const email = useQuery(sessionQuery).data?.user.email ?? null
  const client = useQuery(publicClientQuery(request.clientId))
  const [loadedAt] = useState(() => Date.now())
  const [leaving, setLeaving] = useState<"allow" | "cancel" | null>(null)
  const decide = useMutation({
    mutationFn: (accept: boolean) => postConsent(accept, query),
    onSuccess: (url, accept) => {
      setLeaving(accept ? "allow" : "cancel")
      window.location.assign(url)
    },
  })

  if (request.expiresAt !== null && request.expiresAt <= loadedAt) return <OAuthExpired />
  if (client.isError)
    return <OAuthExpired detail={isExpiredLinkError(client.error) ? undefined : client.error.message} />
  if (client.isPending) return <p className="text-muted">Looking up the app…</p>
  if (decide.error && isExpiredLinkError(decide.error)) return <OAuthExpired />

  const who = consentClient(request, client.data)
  const busy = decide.isPending || leaving !== null
  return (
    <>
      <p className="mono-data text-muted">
        {who.kind === "unverified" ? "CONNECT AN UNVERIFIED APP" : "CONNECT AN APP"}
      </p>
      <CardTitle className="mt-1 break-words">
        {who.kind === "verified" ? (
          <>
            <b>{who.name}</b> ({who.host})
          </>
        ) : who.kind === "local" ? (
          <>
            <b>{who.name}</b> on this computer
          </>
        ) : (
          <>
            An app on <b className="break-all">{who.host}</b>
          </>
        )}{" "}
        wants to access your Minga workspaces
      </CardTitle>
      {who.kind === "unverified" && who.calls && (
        // the registered name is self-declared: quoted, never as the title
        <p className="mt-1.5 text-sm text-muted break-words">It calls itself “{who.calls}”.</p>
      )}
      <ul className="mt-4 grid gap-2.5 text-sm leading-relaxed text-ink-2">
        <li>
          It can read your projects, change plans, capture items, answer questions and message your leads, as
          you.
        </li>
        <li>Approvals of plans and outward actions stay here in Minga.</li>
      </ul>
      <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-control bg-ground p-4 text-sm">
        <dt className="text-muted">Signed in as</dt>
        <dd className="min-w-0 font-medium break-all">{email ?? "you"}</dd>
      </dl>
      {who.kind === "verified" ? (
        // a hosted connector's authorize link works in anyone's browser: someone who started it in their own
        // account could send it to you (decision 0046 §9)
        <p
          role="alert"
          className="mt-3 rounded-card border-[1.5px] border-risk bg-risk-soft p-3 text-sm text-risk-text"
        >
          {`Only allow if you just clicked Connect in ${who.name} yourself. If someone sent you this link, cancel.`}
        </p>
      ) : who.kind === "unverified" ? (
        // any https host may register (decision 0046, amended 2026-10-21): this page is the safeguard
        <div
          role="alert"
          className="mt-3 grid gap-1.5 rounded-card border-[1.5px] border-risk bg-risk-soft p-3 text-sm text-risk-text"
        >
          <p className="font-medium">Minga hasn't verified this app.</p>
          <p>
            If you allow, it gets access to your Minga data and acts as you. Only continue if you started this
            connection yourself, on <b className="break-all">{who.host}</b>. If someone sent you this link,
            cancel.
          </p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted">
          It will send you back to <span className="font-mono">{request.redirectHost ?? "the app"}</span>.
          Allow only if you just started this connection yourself.
        </p>
      )}
      {decide.error && <ErrorText className="mt-4">{decide.error.message}</ErrorText>}
      <div className="mt-6 grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={() => decide.mutate(false)} disabled={busy}>
          {leaving === "cancel" || (decide.isPending && decide.variables === false)
            ? "Cancelling…"
            : "Cancel"}
        </Button>
        <Button onClick={() => decide.mutate(true)} disabled={busy}>
          {leaving === "allow" || (decide.isPending && decide.variables === true) ? "Allowing…" : "Allow"}
        </Button>
      </div>
    </>
  )
}
